import { ClipboardPlus, SquarePen, X } from 'lucide-react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { twJoin } from 'tailwind-merge';

import { AskExpertButton, useAskExpertHandoff } from './AskExpertButton';

import {
  Actionsheet,
  ActionsheetBackdrop,
  ActionsheetContent,
  ActionsheetDragIndicator,
  ActionsheetDragIndicatorWrapper,
  ActionsheetScrollView,
} from '@/components/ui/actionsheet';
import { GlassIconButton } from '@/components/ui/glass';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { View } from '@/components/ui/view';
import { VStack } from '@/components/ui/vstack';
import type { AskTurnContext } from '@/services/ask/api';
import { TOUCH_TARGET_MIN_H } from '@/util/accessibleTouchTarget';

/** The summary's heading, with a close control that returns to what was there before. */
export function AskSummaryHeader({ onClose }: { onClose: () => void }) {
  return (
    <HStack className="items-center gap-2 py-2">
      <Icon as={ClipboardPlus} size={20} className="text-primary-500" />

      <Text className="flex-1 font-semibold text-lg text-typography-900">Summary</Text>

      <GlassIconButton
        icon={X}
        onPress={onClose}
        accessibilityLabel="Close the summary"
        className="-mr-2 web:h-12 web:w-12"
      />
    </HStack>
  );
}

/** The planned question and one fact per line, shared by the sheet and the desktop pane. */
export function AskSummaryBody({
  context,
  turnId,
  conversationId,
  onAskExpert,
}: {
  context: AskTurnContext;
  turnId: string;
  conversationId: string;
  onAskExpert?: () => void;
}) {
  return (
    <VStack className="gap-3">
      <Text className="text-base text-typography-900">{context.question}</Text>

      {context.facts.length > 0 ? (
        <VStack className="gap-1">
          {context.facts.map((fact, index) => (
            <Text key={`${fact.label}-${index}`} className="text-sm text-typography-700">
              <Text className="font-semibold text-sm text-typography-900">{fact.label}: </Text>
              {fact.value}
            </Text>
          ))}
        </VStack>
      ) : null}

      <AskExpertButton
        conversationId={conversationId}
        turnId={turnId}
        questionText={context.question}
        surface="summary"
        onHandoff={onAskExpert}
      />
    </VStack>
  );
}

/**
 * The "Start an inquiry" pill above the composer. It goes straight to the
 * General Inquiry composer with the latest turn's planned question filled in;
 * after a search that found results it is the reader's only way to an expert.
 * The summary behind that question is in the header's options menu.
 *
 * The label is white in both themes, as on the brand Pill: `primary-500` is
 * the same fill in both, and the theme's own `typography-0` turns near-black in
 * dark, which falls below AA at this size.
 *
 * The pill is drawn at 32px inside a 48px pressable whose extra height is
 * taken back with negative margin. react-native-web ignores `hitSlop`, so only
 * a box this size is a full target on web, and the margin keeps the control
 * as compact on screen as it looks.
 *
 * Its pen icon is Ask an Expert's, since both lead to the same handoff. The
 * icon is sized with a numeric size rather than size classes: lucide copies
 * the icon's classes onto every shape inside it, so on web a height and width
 * class stretches its shapes to the full icon box.
 */
export function AskStartInquiryButton({
  conversationId,
  turnId,
  questionText,
}: {
  conversationId: string;
  turnId: string;
  questionText: string;
}) {
  const handOff = useAskExpertHandoff();

  return (
    <Pressable
      onPress={() => handOff({ conversationId, turnId, questionText, surface: 'composer' })}
      accessibilityRole="button"
      accessibilityLabel="Start an inquiry"
      accessibilityHint="Opens a new inquiry with this question filled in"
      className={twJoin(
        'z-10 -my-2 justify-center self-start web:cursor-pointer',
        TOUCH_TARGET_MIN_H
      )}>
      <View className="rounded-xl bg-primary-500 px-3 py-2">
        <HStack className="items-center gap-2">
          <Icon as={SquarePen} size={16} className="text-typography-white" />

          <Text className="font-semibold text-xs uppercase text-typography-white">
            Start an inquiry
          </Text>
        </HStack>
      </View>
    </Pressable>
  );
}

type AskSummarySheetProps = {
  context: AskTurnContext;
  turnId: string;
  conversationId: string;
  isOpen: boolean;
  onClose: () => void;
};

/** The summary on compact layouts, which have no detail pane to hold it. */
export function AskSummarySheet({
  context,
  turnId,
  conversationId,
  isOpen,
  onClose,
}: AskSummarySheetProps) {
  const { bottom } = useSafeAreaInsets();

  return (
    <Actionsheet isOpen={isOpen} onClose={onClose}>
      <ActionsheetBackdrop />

      <ActionsheetContent className="w-full items-stretch rounded-t-2xl px-5">
        <ActionsheetDragIndicatorWrapper>
          <ActionsheetDragIndicator />
        </ActionsheetDragIndicatorWrapper>

        <AskSummaryHeader onClose={onClose} />

        <ActionsheetScrollView contentContainerStyle={{ paddingBottom: bottom + 16 }}>
          <AskSummaryBody
            context={context}
            turnId={turnId}
            conversationId={conversationId}
            onAskExpert={onClose}
          />
        </ActionsheetScrollView>
      </ActionsheetContent>
    </Actionsheet>
  );
}
